<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { browser } from '$app/environment';
  import gsap from 'gsap';
  import { invalidateAll } from '$app/navigation';

  interface Props {
    scrollTop?: number;
    element: HTMLDivElement;
    elementToMove: HTMLDivElement;
    distThreshold?: number;
    distMax?: number;
    // export let distReload = 50;
    refreshTimeout?: number;
    pullToRefreshVerticalOffset?: number;
    pullToRefreshAction?: (() => void) | undefined;
  }

  let {
    scrollTop = 0,
    element,
    elementToMove,
    distThreshold = 70,
    distMax = 100,
    refreshTimeout = 500,
    pullToRefreshVerticalOffset = 0,
    pullToRefreshAction = undefined,
  }: Props = $props();

  let _state = $state<'pending' | 'pulling' | 'releasing' | 'refreshing'>('pending');
  let pullStartY = 0;
  let pullMoveY = 0;
  let dist = 0;
  let distResisted = $state(0);
  let isEnabled = false;

  let refreshingTween = $state<gsap.core.Tween | undefined>();

  let icon = $state<SVGSVGElement | null>(null);

  const resistanceFunction = (t: number): number => {
    return Math.min(1, t / 5.5);
  };

  const onTouchStart = (e: TouchEvent): void => {
    isEnabled = shouldPullToRefresh();
    if (isEnabled && _state === 'pending') {
      pullStartY = e.touches[0].screenY;
    }
  };

  const onTouchMove = (e: TouchEvent): void => {
    pullMoveY = e.touches[0].screenY;
    if (!isEnabled) return;

    if (_state === 'pending') {
      _state = 'pulling';
    }

    if (pullStartY && pullMoveY) {
      dist = pullMoveY - pullStartY;
    }

    if (dist > 0) {
      if (e.cancelable) {
        e.preventDefault();
      }

      distResisted = resistanceFunction(dist / distThreshold) * Math.min(distMax, dist);

      if (_state === 'pulling' && distResisted > distThreshold) {
        _state = 'releasing';
      }

      if (_state === 'releasing' && distResisted < distThreshold) {
        _state = 'pulling';
      }
    }
  };

  function onTouchEnd(): void {
    if (_state === 'releasing' && distResisted > distThreshold) {
      _state = 'refreshing';

      setTimeout(() => {
        if (pullToRefreshAction) {
          pullToRefreshAction();
        }
        invalidateAll().then(() => {
          resetPullToRefresh();
        });
      }, refreshTimeout);
    } else {
      resetPullToRefresh();
    }
  }

  function resetPullToRefresh(): void {
    _state = 'pending';
    pullStartY = pullMoveY = 0;
    dist = distResisted = 0;
  }

  function shouldPullToRefresh(): boolean {
    return scrollTop <= 0;
  }

  onMount(() => {
    if (!browser) {
      return;
    }
  });

  const init = () => {
    element.addEventListener('touchstart', onTouchStart, { passive: true });
    element.addEventListener('touchmove', onTouchMove, { passive: true });
    element.addEventListener('touchend', onTouchEnd, { passive: true });
  };

  onDestroy(() => {
    if (!browser || !element) {
      return;
    }

    element.removeEventListener('touchstart', onTouchStart);
    element.removeEventListener('touchmove', onTouchMove);
    element.removeEventListener('touchend', onTouchEnd);
  });

  $effect(() => {
    if (element) {
      init();
    }
  });

  let progress = $derived(Math.max(0, Math.min(1, distResisted / distThreshold)));

  $effect(() => {
    if (_state !== 'pending' && progress > 0 && progress < 1) {
      gsap.to(elementToMove, {
        y: Math.min(progress * distMax),
        duration: 0.05,
      });
      gsap.to(icon, {
        opacity: progress,
        filter: `blur(${10 - progress * 10}px)`,
        y: 25 * progress,
        rotation: -180 * progress,
        duration: 0.05,
      });
    }
  });

  $effect(() => {
    if (_state !== 'pending') {
      gsap.set(elementToMove, {
        pointerEvents: 'none',
      });
    }
  });

  $effect(() => {
    if (_state === 'refreshing') {
      gsap.to(icon, {
        y: 0,
        duration: 0.2,
      });
      refreshingTween = gsap.to(icon, {
        rotateZ: -36000,
        duration: 100,
        repeat: -1,
        ease: 'linear',
      });
      gsap.to(elementToMove, {
        y: 65,
        duration: 0.2,
      });
    }
  });

  $effect(() => {
    if (browser && _state === 'releasing') {
      refreshingTween?.kill();
      gsap.to(icon, {
        opacity: progress,
        filter: `blur(0px)`,
        y: 25,
        rotation: -180,
        duration: 0.1,
      });
      gsap.to(elementToMove, {
        y: Math.min(progress * distMax),
        duration: 0.1,
      });
    }
  });

  $effect(() => {
    if (_state === 'pending') {
      refreshingTween?.kill();
      if (elementToMove) {
        gsap.set(elementToMove, {
          pointerEvents: 'auto',
        });
        gsap.to(elementToMove, {
          y: 0,
          duration: 0.2,
        });
      }
      if (icon) {
        gsap.to(icon, {
          opacity: 0,
          y: -20,
          filter: `blur(10px)`,
          duration: 0.2,
        });
      }
    }
  });
</script>

<div
  style="top: {pullToRefreshVerticalOffset}px"
  class="pointer-events-none absolute left-0 z-50 grid h-12 w-full place-items-center text-center text-gray-500 transition-all">
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 18 18"
    class="size-8 opacity-0"
    bind:this={icon}
    ><title>arrow-dotted-rotate-anticlockwise</title><g fill="currentColor" stroke="currentColor"
      ><polyline
        points="1.872 3.305 2.28 6.25 5.224 5.843"
        fill="none"
        stroke="currentColor"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="1.5"></polyline
      ><path
        d="M16.232,8.484c-.265-3.763-3.401-6.734-7.232-6.734-3.031,0-5.627,1.86-6.71,4.5"
        fill="none"
        stroke="currentColor"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="1.5"></path
      ><circle cx="14.127" cy="14.127" r=".75" data-stroke="none" stroke="none"></circle><circle
        cx="9"
        cy="16.25"
        r=".75"
        data-stroke="none"
        stroke="none"></circle
      ><circle cx="3.873" cy="14.127" r=".75" data-stroke="none" stroke="none"></circle><circle
        cx="1.75"
        cy="9"
        r=".75"
        data-stroke="none"
        stroke="none"></circle
      ><circle cx="15.698" cy="11.774" r=".75" data-stroke="none" stroke="none"></circle><circle
        cx="11.774"
        cy="15.698"
        r=".75"
        data-stroke="none"
        stroke="none"></circle
      ><circle cx="6.226" cy="15.698" r=".75" data-stroke="none" stroke="none"></circle><circle
        cx="2.302"
        cy="11.774"
        r=".75"
        data-stroke="none"
        stroke="none"></circle
      ></g
    ></svg>
</div>
